/**
 * Links into a page section that open the page at the top and then scroll
 * down to the section smoothly, instead of jumping there.
 *
 * The hash names the section but matches no id, so the browser leaves the
 * page at the top. The widget's script runs on every woodsidebible.org page;
 * on load it finds the section, waits for the page above it to settle (the
 * hero and other widgets render late and push it down), and scrolls to it,
 * stopping below the theme's fixed header.
 */
export const SECTION_HASHES = {
  /** The announcements widget on a campus page */
  announcements: 'announcements',
} as const;

const SECTION_IDS: Record<string, string> = {
  [SECTION_HASHES.announcements]: 'announcements-widget-root',
};

/** Space left between the fixed header and the section */
const GAP = 16;
/** How long to wait for the section and the layout above it, at most */
const MAX_WAIT_MS = 5000;
/** The layout counts as settled once the section has not moved for this long */
const SETTLE_MS = 400;

let started = false;

export function scrollToSectionFromHash(): void {
  if (started || typeof window === 'undefined') return;
  const id = SECTION_IDS[window.location.hash.slice(1)];
  if (!id) return;
  started = true;

  const startedAt = performance.now();
  let lastTop: number | null = null;
  let stableSince = 0;
  // Someone who scrolls on their own while we wait has taken over
  let userScrolled = false;
  const stop = () => {
    userScrolled = true;
  };
  window.addEventListener('wheel', stop, { once: true, passive: true });
  window.addEventListener('touchmove', stop, { once: true, passive: true });
  window.addEventListener('keydown', stop, { once: true });

  const tick = (now: number) => {
    if (userScrolled) return;
    const el = document.getElementById(id);
    const top = el && el.offsetHeight > 0 ? el.getBoundingClientRect().top + window.scrollY : null;
    if (top !== null) {
      if (lastTop === null || Math.abs(top - lastTop) > 1) {
        lastTop = top;
        stableSince = now;
      } else if (now - stableSince >= SETTLE_MS) {
        return scrollTo(top);
      }
    }
    if (now - startedAt > MAX_WAIT_MS) {
      if (top !== null) scrollTo(top);
      return;
    }
    requestAnimationFrame(tick);
  };

  const begin = () => requestAnimationFrame(tick);
  if (document.readyState === 'complete') begin();
  else window.addEventListener('load', begin, { once: true });
}

function scrollTo(sectionTop: number) {
  const header = document.getElementById('header-outer');
  const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  window.scrollTo({
    top: Math.max(0, sectionTop - headerBottom - GAP),
    behavior: reduce ? 'auto' : 'smooth',
  });
}
